import { useEffect, useState, Suspense } from 'react';

import { GraphQLClient } from 'graphql-request';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AppContext } from './App.context';
import { useDb, useWorkerMessages, useInstalled, useResize } from './App.hooks';
import config from './config.window';
import { Template } from './config.type';
import {
	useDeviceInformation,
	useDocumentTitle,
	usePrintStatus,
} from './hooks/use-element';

import { useOnlineStatus } from './hooks/use-online';
import { useServiceWorker } from './hooks/use-service-worker';
import { Password, usePassword } from './views/password/Password';

// Theme
import AppPoets from './templates/poets/App';
import AppMagazine from './templates/magazine/App';
import AppMorningstar from './templates/morningstar/App';
import AppPadi from './templates/padi/App';
import AppSections from './templates/sections/App';
import AppMagshop from './templates/magshop/App';
import AppBasic from './templates/basic/App';

import { ALERT_TIMEOUT_KEYS } from './constants';

import { About } from './components/about/About';

import { Tracking } from './tracking/Tracking';
import LocalStorageKV from './services/kv/LocalStorageKV';
import {
	ReactQueryDevtoolsProduction,
	queryClient,
} from './services/query-client/QueryClient';
import { getGraphQLClient } from './views/home/auth/Authentication.hooks';
import { Assets } from './assets';

declare global {
	interface Window {
		onbeforeinstallprompt?: any;
		toggleDevtools?: any;
	}
}

function App() {
	const { contentProtection } = config.app;
	const [showDevtools, setShowDevtools] = useState(false);
	const { host, appKey, languages, language, title } = config.build;

	const [graphQLClient, setGraphQLClient] = useState<GraphQLClient>(
		getGraphQLClient(),
	);

	useBoot();

	const kv = new LocalStorageKV('cf-kv');

	const isSupportInstallPrompt =
		typeof window.onbeforeinstallprompt === 'object';
	const onlineStatus = useOnlineStatus(host);
	const device = useDeviceInformation();
	const [contentCSS, setContentCSS] = useState('');
	const [deferredPrompt, setDeferredPrompt] = useState<any>(undefined);
	const [showDialog, setShowDialog] = useState<boolean>(false);
	const isInstalled = useInstalled(device?.type);
	const { shouldDisplayPassword, onSuccessPassword } = usePassword();

	useEffect(() => {
		window.toggleDevtools = () => setShowDevtools((old) => !old);
	}, []);
	useEffect(() => {
		localStorage.setItem('app-mode', `${import.meta.env.MODE}`);

		if (isSupportInstallPrompt) {
			window.addEventListener('beforeinstallprompt', (e) => {
				// Prevents the default mini-infobar or install dialog from appearing on mobile
				e.preventDefault();
				setDeferredPrompt(e);
				return false;
			});
		}
		if (contentProtection) {
			document.body.oncontextmenu = (e) => {
				e.preventDefault();
				return false;
			};
			setContentCSS(`@media print {
				html, body {
					   display: none;  /* hide whole page */
				}
			}`);
		}

		// Clear timeouts
		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.ID)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.ID);
		}

		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.DATETIME)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.DATETIME);
		}
	}, []);
	useResize();
	useDocumentTitle(title);

	const isPrinting = usePrintStatus();
	const { db } = useDb();

	const sw = useServiceWorker();
	useWorkerMessages({ db });

	const [lang, setLang] = useState(
		localStorage.getItem('language')
			? localStorage.getItem('language')
			: language,
	);

	const onLanguageChange = (language: string) => {
		localStorage.setItem('language', language);
		setLang(language);
	};

	if (onlineStatus === null || device === null || !db || !kv) {
		return null;
	}

	if (shouldDisplayPassword) {
		return <Password onSuccess={onSuccessPassword} />;
	}

	// If you are in production you need to wait for the service worker to load
	if (
		import.meta.env.MODE === 'production' &&
		'serviceWorker' in navigator &&
		!sw
	) {
		return null;
	}

	const templateView = getTemplateView(config.app.template);

	return (
		<AppContext.Provider
			value={{
				db,
				appKey,
				graphQLClient,
				baseUrl: config?.build?.subdirectory ?? '/',
				lang: lang ?? 'en',
				languages,
				onLanguageChange,
				onlineStatus,
				device,
				isPrinting,
				isInstalled,
				sw,
				isSupportInstallPrompt,
				deferredPrompt,
				kv,
				clearCache,
				rebuildGraphQLClient: () => {
					queryClient.clear();
					setGraphQLClient(getGraphQLClient());
				},
				showDialog,
				setShowDialog: (v: boolean) => {
					setShowDialog(v);
				},
				parseGraphQLClientError: (err: any) => {
					const reqRes = JSON.parse(JSON.stringify(err));
					return new Error(reqRes.response.errors[0].message);
				},
			}}
		>
			<QueryClientProvider client={queryClient}>
				<Tracking>
					<Assets>{templateView}</Assets>
				</Tracking>

				{contentCSS ? <style>{contentCSS}</style> : null}
				<About
					show={showDialog}
					setShow={(v: boolean) => {
						setShowDialog(v);
					}}
				/>
				<ReactQueryDevtools initialIsOpen={false} />
				{showDevtools && (
					<Suspense fallback={null}>
						<ReactQueryDevtoolsProduction />
					</Suspense>
				)}
			</QueryClientProvider>
		</AppContext.Provider>
	);
}

async function clearCache(): Promise<void> {
	const promises = [];
	const keyList = await caches.keys();
	for (const key of keyList) {
		promises.push(caches.delete(key));
	}

	await Promise.all(promises);
}

function useBoot() {
	const setBuildValues = () => {
		localStorage.setItem('app_key', config.build.appKey);
		localStorage.setItem('version', config.build.version);
	};
	useEffect(() => {
		const version = localStorage.getItem('version');
		const appKey = localStorage.getItem('app_key');
		if (!appKey || !version) {
			clearLocalStorage();
			setBuildValues();
			return;
		}

		if (
			appKey !== config.build.appKey ||
			version !== config.build.version
		) {
			clearLocalStorage();
			setBuildValues();
			return;
		}
	}, []);
}

function clearLocalStorage() {
	const ignoredKeys = new Set(['favorites']);
	for (const key in localStorage) {
		if (ignoredKeys.has(key)) {
			continue;
		}

		localStorage.removeItem(key);
	}
}

function getTemplateView(template: Template) {
	if (template?.type === 'magazine') {
		return <AppMagazine />;
	}

	if (template?.type === 'padi') {
		return <AppPadi />;
	}

	if (template?.type === 'sections') {
		return <AppSections />;
	}

	if (template?.type === 'poets') {
		return <AppPoets />;
	}

	if (template?.type === 'magshop') {
		return <AppMagshop />;
	}

	if (template?.type === 'morningstar') {
		return <AppMorningstar />;
	}

	return <AppBasic />;
}

export default App;
